<template>
  <div class="d-flex content-container h-100 overflow-hidden">
    <page-header :page-title="chapter.name ? chapter.name : ''" @back="backToMaster"></page-header>

    <div class="w-100 d-flex justify-content-center align-items-center loader" v-if="loading">
      <b-spinner variant="secondary" type="grow" />
    </div>

    <div class="d-flex content-container align-items-center w-100 overflow-hidden" v-else>
      <div class="container-fluid px-0">
        <div v-if="pageBlockHeaderIsHidden">
          <b-container fluid="" class="max-width">
            <div class="row justify-content-center">
              <div class="col-12 col-md-10 col-lg-8">
                <block-header :title="header.title" overline="" subtitle="" />
                <hr />
                <div class="div d-flex justify-content-end">
                  <privacy-alert
                    buttonSize="sm"
                    :popupContent="trans('global.only_you_content')"
                    :popupButton="trans('global.more_info')"
                    :popupHref="{ name: 'privacy-legend' }"
                    popoverId="privacyPopover"
                    v-bind:targetBlank="true"
                  >
                    <img src="/images/icon_private.svg" width="24px" class="pr-1" />
                    {{ trans("global.only_you") }}
                  </privacy-alert>
                </div>
              </div>
            </div>
          </b-container>
        </div>

        <block-container
          v-for="(block, index) in blocks"
          :key="index"
          :type="block.type"
          :data="block.data"
          :module="module"
          :course="course"
          :isMission="isMission"
          :user-id="getUserId"
          :id="index"
        />

        <div v-if="isInstruction">
          <b-container fluid="" class="max-width">
            <div class="row justify-content-center">
              <div class="col-12 col-md-10 col-lg-8">
                <mission-scheduler :chapterName="chapter.name" @onSelectedDay="onSelectedDay" />
              </div>
            </div>
          </b-container>
        </div>

        <div>
          <b-container fluid="" class="max-width">
            <div class="row justify-content-center">
              <div class="col-12 col-md-10 col-lg-8 pb-5 block-text" v-if="counter">
                <div class="text-center text-bold">
                  <p v-if="!isInstruction">{{ counter.current }} / {{ counter.total }}</p>
                </div>
                <hr />
                <div class="row mt-4" v-if="nav">
                  <div class="col-6">
                    <b-button
                      variant="link"
                      class="text-primary px-0"
                      @click="prevPage"
                      v-if="nav.prev"
                      >{{ nav.prev.object_label }}</b-button
                    >
                  </div>
                  <div class="col-6 text-right">
                    <b-button variant="secondary" @click="nextPage" :disabled="nextLoading">
                      <b-spinner small type="grow" v-if="nextLoading" />
                      {{ nav.next.object_label }}
                    </b-button>
                  </div>
                </div>
              </div>
            </div>
          </b-container>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import navMixin from "../mixins/navMixin";
import PageHeader from "../organisms/PageHeader";
import BlockHeader from "../molecules/BlockHeader";
import BlockContainer from "../organisms/master-page/BlockContainer";
import MissionScheduler from "../organisms/master-page/MissionScheduler";
import PrivacyAlert from "../molecules/PrivacyAlert";

export default {
  name: "MasterPageContainer",
  components: { BlockContainer, PageHeader, BlockHeader, MissionScheduler, PrivacyAlert },
  data() {
    return {
      blocks: [],
      loading: true,
      nextLoading: false,
      nav: null,
      back: null,
      chapter: {},
      module: {},
      moduleId: null,
      course: {},
      programId: null,
      isMission: "",
      isInstruction: "",
      missionStatus: {},
      missionId: null,
      header: {},
      counter: null,
      defaultSelectedDay: null,
      prevRouteName: "",
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.prevRouteName = from.name;
    });
  },
  beforeRouteUpdate(to, from, next) {
    this.prevRouteName = "";
    next();
  },
  mounted() {
    this.loading = true;
    this.loadMoodsList();
    this.getPage();
    if (this.isInstruction && this.missionStatus.status === "scheduled") {
      trackEvent("MissionInstructionRecap", {});
    }
  },
  watch: {
    $route(to, from) {
      this.getPage();
    },
  },
  methods: {
    ...mapActions({
      loadMoodsList: "moodsAndReactions/loadMoodsList",
    }),
    getPage() {
      this.loading = true;
      this.programId = this.$route.params.programId;
      this.moduleId = this.$route.params.moduleId;
      this.$axios
        .get(
          `/api/rest/programs/${this.programId}/modules/${this.moduleId}/pages/${this.$route.params.id}`
        )
        .then((res) => {
          this.loading = false;
          this.blocks = res.data.content;
          this.nav = res.data.nav;
          this.back = res.data.back;
          this.chapter = res.data.chapter;
          this.module = res.data.module;
          this.course = res.data.program;
          this.header = { title: res.data.title };
          this.counter = res.data.counter;
          this.isMission = res.data.isMission;
          this.isInstruction = res.data.isInstructionPage;
          this.missionId = res.data.missionId;
          this.missionStatus = res.data.missionStatus;

          trackEvent("CoursePageView", {
            "page-id": this.$route.params.id,
            "module-id": this.module.id,
            "course-id": this.course.id,
            "course-name": this.course.name,
            "is-augmented-webinar": this.course.isAugmented,
            "is-first-page": this.module.isFirstPage,
            "is-mission": this.isMission,
          });
        });
    },
    async completePage(pageId) {
      try {
        await this.$axios.post(
          `/api/rest/programs/${this.programId}/modules/${this.moduleId}/pages/${pageId}/tracking/completion`
        );
      } catch (err) {
        console.log(err.response.data);
      }
    },
    backToMaster() {
      this.pageTrackingEnd();
      if (this.backButtonRoutes.includes(this.prevRouteName)) {
        this.$router.push({ name: this.prevRouteName });
      } else {
        this.$router.push(this.parseMasterNav(this.back));
      }
    },
    pageTrackingEnd() {
      try {
        this.$axios.post(
          `/api/rest/programs/${this.programId}/modules/${this.moduleId}/pages/${this.$route.params.id}/tracking/close`
        );
      } catch (err) {
        console.log(err.response.data);
      }
    },
    blockBackground(block) {
      let background = "";

      switch (block.background) {
        case "primary":
          background = "primary-block";
          break;
        case "grigio":
          background = "grey-block";
          break;
      }

      return background;
    },
    async nextPage() {
      this.nextLoading = true;
      try {
        await this.completePage(this.$route.params.id);
      } catch (err) {
        console.error(err);
      }
      if (this.isInstruction) {
        if (!this.missionStatus) {
          trackEvent("ScheduleMission", {
            selectedDay: this.defaultSelectedDay.label,
          });
        } else {
          trackEvent("MissioneReSchedule", {
            selectedDay: this.defaultSelectedDay.label,
          });
        }
      }
      this.nextLoading = false;
      this.$router.push(this.parseMasterNav(this.nav.next));
    },
    prevPage() {
      this.pageTrackingEnd();
      this.$router.push(this.parseMasterNav(this.nav.prev));
    },
    onSelectedDay(day) {
      this.defaultSelectedDay = day;
    },
  },
  computed: {
    ...mapGetters("user", ["getUserId"]),

    pageBlockHeaderIsHidden: function () {
      return this.blocks[0].type !== "posts_collector";
    },
  },
  mixins: [navMixin],
};
</script>

<style scoped lang="scss">
.grey-block {
  background-color: $color-light;
}

.primary-block {
  background-color: $color-blue;
  ::v-deep {
    .card {
      .textarea-overview {
        color: initial !important;
        opacity: 0.4;
      }
      .text-view {
        color: initial !important;
      }
    }
    .textarea-overview {
      color: rgba($color-white, 0.4) !important;
    }
    .text-view {
      color: $color-white !important;
    }
    .block-header,
    .block-text {
      color: $color-white !important;

      ::v-deep a {
        color: $color-white !important;
      }
    }
    hr {
      border-color: $color-white;
    }
  }
}

.loader {
  min-height: 100vh;
}
</style>
